/**
 * Ecranul „Astăzi" — prima pagină după autentificare.
 *
 * Răspunde la întrebarea „ce se întâmplă acum în restaurantul meu?", cu date reale:
 * vizualizări de meniu, scanări QR, starea produselor, a ecranelor și activitatea recentă.
 * Fiecare bloc are stare de încărcare (skeleton) și se degradează elegant la eroare.
 */
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Icon,
  InlineAlert,
  KpiCard,
  PageHeader,
  Skeleton,
  StatusDot,
  cx,
} from '../components/ui.js';
import { api } from '../lib/api.js';
import { AUDIT_LABELS, auditLabel, formatNumber, relativeTime, useResource } from '../lib/hooks.js';
import { useSession } from '../lib/session.js';

interface TodayData {
  date: string;
  menuViews: { today: number; yesterday: number; last7Days: number };
  qrScans: { today: number; yesterday: number; last7Days: number };
  products: { total: number; available: number; unavailable: number; featured: number };
  displays: { total: number; online: number; offline: number; neverSeen: number };
  menus: { total: number; published: number; drafts: number };
  qrCodes: { total: number; scansAllTime: number };
  promotions: { active: number; scheduled: number };
  team: { members: number };
  topProducts: Array<{ name: string; views: number; qrViews: number; displayImpressions: number }>;
  recentActivity: Array<{ action: string; label: string | null; severity: string; createdAt: string; actorRole: string | null }>;
  busiestHours: Array<{ hour: number; scans: number }>;
}

function delta(today: number, yesterday: number): { value: string; positive: boolean } | undefined {
  if (!yesterday) return undefined;
  const change = Math.round(((today - yesterday) / yesterday) * 100);
  if (change === 0) return { value: '0%', positive: true };
  return { value: `${change > 0 ? '+' : ''}${change}% față de ieri`, positive: change > 0 };
}

export function DashboardPage() {
  const session = useSession();
  const state = useResource<TodayData>(() => api.get<TodayData>('/dashboard/today'), [session.activeAccountId]);
  const data = state.data;

  return (
    <div className="page-enter">
      <PageHeader
        title="Astăzi"
        subtitle={new Intl.DateTimeFormat('ro-RO', { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date())}
        actions={
          <>
            <Link to="/app/locations">
              <Button variant="secondary" icon={<Icon name="pin" size={17} />}>
                Locații
              </Button>
            </Link>
            <Link to="/app/team">
              <Button icon={<Icon name="plus" size={17} />}>Invită colegi</Button>
            </Link>
          </>
        }
      />

      {state.error ? (
        <div className="mb-6">
          <InlineAlert tone="warning" title="Statisticile nu s-au putut încărca">
            {state.error}{' '}
            <button type="button" className="font-medium underline" onClick={() => void state.reload()}>
              Reîncearcă
            </button>
            {session.user?.isPlatformAdmin ? (
              <>
                {' '}
                Contul de administrator nu aparține unui restaurant. Gestionezi platforma din secțiunea{' '}
                <Link to="/admin" className="font-medium underline">
                  Platformă
                </Link>
                .
              </>
            ) : null}
          </InlineAlert>
        </div>
      ) : null}

      {/* KPI-uri */}
      <section aria-label="Indicatori principali" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {state.loading || !data
          ? Array.from({ length: 4 }).map((_, index) => (
              <Card key={index} className="flex flex-col gap-3">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-20" />
                <Skeleton className="h-3 w-32" />
              </Card>
            ))
          : (
              <>
                <KpiCard
                  label="Vizualizări meniu azi"
                  value={formatNumber(data.menuViews.today)}
                  trend={delta(data.menuViews.today, data.menuViews.yesterday)}
                  hint={`${formatNumber(data.menuViews.last7Days)} în ultimele 7 zile`}
                />
                <KpiCard
                  label="Scanări QR azi"
                  value={formatNumber(data.qrScans.today)}
                  trend={delta(data.qrScans.today, data.qrScans.yesterday)}
                  hint={`${formatNumber(data.qrCodes.total)} coduri active`}
                />
                <KpiCard
                  label="Produse disponibile"
                  value={`${formatNumber(data.products.available)}/${formatNumber(data.products.total)}`}
                  hint={data.products.unavailable > 0 ? `${data.products.unavailable} marcate indisponibile` : 'Toate produsele sunt active'}
                />
                <KpiCard
                  label="Ecrane online"
                  value={`${formatNumber(data.displays.online)}/${formatNumber(data.displays.total)}`}
                  hint={data.displays.offline > 0 ? `${data.displays.offline} offline` : 'Toate ecranele răspund'}
                />
              </>
            )}
      </section>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        {/* Scanări pe ore */}
        <Card className="lg:col-span-2">
          <CardHeader
            title="Când îți deschid clienții meniul"
            subtitle="Scanări QR pe ore, ultimele 30 de zile"
            action={<Badge tone="brand">trafic real</Badge>}
          />
          {state.loading || !data ? (
            <div className="flex h-40 items-end gap-1.5">
              {Array.from({ length: 24 }).map((_, index) => (
                <Skeleton key={index} className={cx('flex-1', index % 3 === 0 ? 'h-24' : 'h-16')} />
              ))}
            </div>
          ) : (
            <HourChart hours={data.busiestHours} />
          )}
        </Card>

        {/* Starea canalelor */}
        <Card>
          <CardHeader title="Canale de afișare" subtitle="Un meniu, toate suprafețele" />
          {state.loading || !data ? (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
            </div>
          ) : (
            <ul className="flex flex-col divide-y divide-ink-100">
              <ChannelRow
                icon="menu"
                label="Meniuri"
                value={`${data.menus.published} publicate`}
                hint={data.menus.drafts ? `${data.menus.drafts} în lucru` : 'toate publicate'}
                tone={data.menus.published ? 'success' : 'neutral'}
              />
              <ChannelRow
                icon="qr"
                label="Coduri QR"
                value={`${data.qrCodes.total}`}
                hint={`${formatNumber(data.qrCodes.scansAllTime)} scanări în total`}
                tone="success"
              />
              <ChannelRow
                icon="display"
                label="Ecrane"
                value={`${data.displays.online} online`}
                hint={data.displays.offline ? `${data.displays.offline} fără semnal` : 'toate conectate'}
                tone={data.displays.offline ? 'warning' : 'success'}
              />
              <ChannelRow
                icon="sparkles"
                label="Promoții"
                value={`${data.promotions.active} active`}
                hint={data.promotions.scheduled ? `${data.promotions.scheduled} programate` : 'fără programări'}
                tone={data.promotions.active ? 'brand' : 'neutral'}
              />
            </ul>
          )}
        </Card>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {/* Top produse */}
        <Card>
          <CardHeader title="Cele mai vizualizate produse" subtitle="Ultimele 7 zile" />
          {state.loading || !data ? (
            <div className="flex flex-col gap-3">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-10" />
              ))}
            </div>
          ) : data.topProducts.length ? (
            <ol className="flex flex-col divide-y divide-ink-100">
              {data.topProducts.map((product, index) => (
                <li key={product.name} className="flex items-center gap-3 py-2.5">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-surface-3 text-micro font-medium text-ink-600">
                    {index + 1}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-body-s text-ink-950">{product.name}</span>
                  <span className="nums text-caption text-ink-400">
                    {formatNumber(product.views + product.qrViews)} vizualizări
                  </span>
                </li>
              ))}
            </ol>
          ) : (
            <EmptyState
              title="Încă nu avem date"
              description="Statisticile apar după primele scanări ale codurilor QR."
            />
          )}
        </Card>

        {/* Activitate recentă */}
        <Card>
          <CardHeader title="Activitate recentă" subtitle="Auditul acțiunilor din cont" />
          {state.loading || !data ? (
            <div className="flex flex-col gap-3">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-10" />
              ))}
            </div>
          ) : data.recentActivity.length ? (
            <ul className="flex flex-col divide-y divide-ink-100">
              {data.recentActivity.map((item, index) => (
                <li key={`${item.action}-${index}`} className="flex items-center gap-3 py-2.5">
                  <StatusDot
                    tone={
                      item.severity === 'critical' ? 'danger' : item.severity === 'warning' ? 'warning' : 'success'
                    }
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-body-s text-ink-950">
                      {AUDIT_LABELS[item.action] ?? auditLabel(item.action)}
                      {item.label ? <span className="text-ink-600"> · {item.label}</span> : null}
                    </span>
                  </span>
                  <span className="text-caption whitespace-nowrap text-ink-400">{relativeTime(item.createdAt)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="Nicio activitate încă" description="Aici vor apărea modificările făcute de echipă." />
          )}
        </Card>
      </div>

      {/* Acțiuni rapide */}
      <Card className="mt-6">
        <CardHeader title="Pași următori" subtitle="Ce are sens să faci acum în cont" />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <QuickAction
            to="/app/locations"
            icon="pin"
            title="Adaugă o locație"
            description="Fiecare locație are propriile QR-uri și ecrane."
          />
          <QuickAction
            to="/app/team"
            icon="users"
            title="Invită echipa"
            description="Manager, editor sau staff — cu permisiuni diferite."
          />
          <QuickAction
            to="/app/settings"
            icon="settings"
            title="Verifică abonamentul"
            description="Limitele planului și consumul actual."
          />
          <QuickAction
            to="/app/notifications"
            icon="bell"
            title="Notificări"
            description="Alerte de securitate și starea canalelor."
          />
        </div>
      </Card>
    </div>
  );
}

function ChannelRow({
  icon,
  label,
  value,
  hint,
  tone,
}: {
  icon: 'menu' | 'qr' | 'display' | 'sparkles';
  label: string;
  value: string;
  hint: string;
  tone: 'success' | 'warning' | 'brand' | 'neutral';
}) {
  return (
    <li className="flex items-center gap-3 py-3">
      <span className="flex h-9 w-9 items-center justify-center rounded-[var(--radius-sm)] bg-surface-3 text-ink-600">
        <Icon name={icon} size={17} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-body-s font-medium text-ink-950">{label}</span>
        <span className="text-caption text-ink-400">{hint}</span>
      </span>
      <Badge tone={tone}>{value}</Badge>
    </li>
  );
}

function QuickAction({ to, icon, title, description }: { to: string; icon: 'pin' | 'users' | 'settings' | 'bell'; title: string; description: string }) {
  return (
    <Link
      to={to}
      className="transition-ui flex flex-col gap-1.5 rounded-[var(--radius-md)] border border-ink-100 p-4 hover:border-brand-100 hover:bg-brand-100/30"
    >
      <span className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)] bg-brand-100/70 text-brand-700">
        <Icon name={icon} size={16} />
      </span>
      <span className="text-body-s font-medium text-ink-950">{title}</span>
      <span className="text-caption text-ink-600">{description}</span>
    </Link>
  );
}

/** Grafic minimal de bare (fără librărie): 24 de ore, normalizate pe maxim. */
function HourChart({ hours }: { hours: Array<{ hour: number; scans: number }> }) {
  const byHour = new Map(hours.map((entry) => [entry.hour, entry.scans]));
  const values = Array.from({ length: 24 }, (_, hour) => byHour.get(hour) ?? 0);
  const max = Math.max(1, ...values);

  const peak = Math.max(...values);

  /*
   * Graficul are 24 de coloane, iar eticheta fiecăreia (un număr de două cifre) stabilea o lățime
   * MINIMĂ de coloană: pe un telefon de 390 px, cele 24 de coloane plus spațiile cereau ~460 px, iar
   * cardul — și cu el pagina întreagă — se lărgea peste ecran (defect măsurat: „Astăzi" se derula
   * lateral). Reparat pe trei căi, toate necesare:
   *   • `min-w-0` pe fiecare coloană, ca să poată coborî sub lățimea etichetei;
   *   • spațiu mai mic între coloane pe ecran mic (`gap-0.5`, apoi `sm:gap-1`);
   *   • etichete mai rare pe ecran mic (din 6 în 6 ore, nu din 3 în 3) — altfel numerele s-ar
   *     suprapune și ar deveni ilizibile oricum.
   * Coloanele rămân egale și barele rămân vizibile; doar informația secundară se rarefiază.
   */
  return (
    <div className="flex h-40 gap-0.5 sm:gap-1" role="img" aria-label="Scanări pe ore în ultimele 30 de zile">
      {values.map((value, hour) => {
        const isPeak = value === peak && peak > 0;
        return (
          <div
            key={hour}
            // `h-full` este obligatoriu: fără el, coloana are înălțime automată, iar bara care
            // folosește `height: %` se calculează față de un părinte nedefinit și dispare.
            className="group flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1.5"
          >
            <div className="relative flex w-full flex-1 items-end">
              <div
                className={cx(
                  'transition-ui w-full rounded-t-[4px]',
                  isPeak ? 'bg-brand-600' : 'bg-brand-100 group-hover:bg-brand-500',
                )}
                style={{ height: `${Math.max(3, (value / max) * 100)}%` }}
              />
            </div>
            <span className="text-micro text-ink-400">
              <span className={hour % 6 === 0 ? 'sm:hidden' : 'hidden'}>{hour}</span>
              <span className={cx('hidden sm:inline', hour % 3 === 0 ? 'opacity-100' : 'opacity-0')}>{hour}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}
